/**
 * Compute the completion status of each FPR wizard step based on formData.
 *
 * Returns an array (one entry per step) of:
 *   'complete' — required fields are filled (or a "none of the above" check)
 *   'partial'  — some data entered but required fields are missing
 *   'empty'    — no data entered at all
 */

function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'boolean') return v === true;
  return false;
}

function anyTruthy(obj, exclude = []) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.entries(obj).some(([k, v]) => {
    if (exclude.includes(k)) return false;
    if (Array.isArray(v)) {
      return v.some((item) =>
        item && typeof item === 'object'
          ? anyTruthy(item)
          : hasValue(item)
      );
    }
    if (v && typeof v === 'object') return anyTruthy(v);
    return hasValue(v);
  });
}

function personalStatus(data) {
  if (data?._skipped) return 'complete';
  if (!data) return 'empty';
  const required = [
    'first_name', 'last_name', 'phone', 'email',
    'uses_emoney', 'tracks_spending',
    'investment_check_frequency', 'investment_sentiment',
  ];
  const allReq = required.every((k) => hasValue(data[k]));
  const anyData = anyTruthy(data);
  if (!anyData) return 'empty';
  if (!allReq) return 'partial';
  return 'complete';
}

/**
 * Life-event sections — completion = at least one box checked OR
 * "none of the above" checked.
 */
function lifeEventStatus(data) {
  if (data?._skipped) return 'complete';
  if (!data) return 'empty';
  if (data.none_of_the_above) return 'complete';
  // Any non-meta truthy value counts as partial-or-complete
  return anyTruthy(data, ['notes']) ? 'complete' : 'empty';
}

function listStatus(data, key = 'entries') {
  if (data?._skipped) return 'complete';
  if (!data) return 'empty';
  const items = data[key] || [];
  const hasAny = items.some(
    (e) => e && Object.values(e).some((v) => hasValue(v))
  );
  if (hasAny) return 'complete';
  return hasValue(data.notes) ? 'partial' : 'empty';
}

function flatStatus(data) {
  if (data?._skipped) return 'complete';
  if (!anyTruthy(data)) return 'empty';
  return 'complete';
}

function scheduleMeetingStatus(data) {
  if (!data) return 'empty';
  const explicitStatus = String(data.meeting_status || '').trim().toLowerCase();
  if (explicitStatus === 'canceled' || explicitStatus === 'cancelled') {
    return data._visited || data.booking_data ? 'partial' : 'empty';
  }
  if (explicitStatus === 'scheduled') return 'complete';
  if (data.booking_detected) return 'complete';
  if (data._visited || data.opened_external) return 'partial';
  return 'empty';
}

const STATUS_FNS = {
  welcome:             () => 'complete',
  personal:            personalStatus,
  family_personal:     lifeEventStatus,
  career_income:       lifeEventStatus,
  financial_planning:  lifeEventStatus,
  accounts:            (d) => listStatus(d, 'entries'),
  income:              (d) => {
    if (d?._skipped) return 'complete';
    if (!d) return 'empty';
    const first = (d.sources || [])[0];
    const firstReq = first && hasValue(first.type) && hasValue(first.amount);
    if (!firstReq) {
      const hasAny = (d.sources || []).some((s) => s && Object.values(s).some((v) => hasValue(v)));
      return hasAny ? 'partial' : 'empty';
    }
    return 'complete';
  },
  debts:               (d) => listStatus(d, 'entries'),
  expenses:            flatStatus,
  documents:           flatStatus,
  review:              () => 'empty',
  schedule_meeting:    scheduleMeetingStatus,
};

export function computeStepStatuses(formData, steps, currentStep) {
  return steps.map((step, idx) => {
    if (step.key === 'welcome') {
      // Welcome has no inputs — mark complete once the user has visited or moved past it.
      return currentStep >= idx ? 'complete' : 'empty';
    }
    const fn = STATUS_FNS[step.key] || flatStatus;
    return fn(formData[step.key]);
  });
}
